/**
 * React Query hooks for admin API operations
 * Provides caching, error handling, and optimistic updates for admin functionality
 */

import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { authenticatedApi } from '../lib/authenticated-api';
import type { UserSearchResult, UserProfile } from '../lib/types/user';

/**
 * Backend response format for admin users list endpoint.
 * Matches the structure returned by /v1/admin/users endpoint.
 */
interface AdminUsersListApiResponse {
  users: UserProfile[];
  pagination: {
    next_cursor?: string;
    has_more: boolean;
    limit: number;
  };
  timestamp: string;
}

// Query keys for React Query caching
export const adminQueryKeys = {
  all: ['admin'] as const,
  users: () => [...adminQueryKeys.all, 'users'] as const,
  usersList: (params?: {
    emailSearch?: string;
    nameSearch?: string;
    limit?: number;
  }) => [...adminQueryKeys.users(), 'list', params] as const,
  user: (uid: string) => [...adminQueryKeys.users(), 'detail', uid] as const,
  userEntitlements: (uid: string) =>
    [...adminQueryKeys.users(), 'entitlements', uid] as const,
  rateLimitStatus: () => [...adminQueryKeys.all, 'rateLimitStatus'] as const,
};

/**
 * Hook for listing users with search, filter, and infinite scroll pagination
 */
export function useAdminUsersList(params?: {
  emailSearch?: string;
  nameSearch?: string;
  limit?: number;
}) {
  return useInfiniteQuery<
    UserSearchResult,
    Error,
    InfiniteData<UserSearchResult>,
    readonly ['admin', 'users', 'list', typeof params],
    string | undefined
  >({
    queryKey: adminQueryKeys.usersList(params),
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) => {
      const response = await authenticatedApi.listUsers({
        ...params,
        cursor: pageParam,
      });
      if (!response.success || !response.data) {
        throw new Error(String(response.error) || 'Failed to fetch users');
      }

      // Transform backend response to match UserSearchResult interface
      // The API returns the data already typed, but we need to access pagination metadata
      const backendData = response.data as unknown as AdminUsersListApiResponse;
      const transformedData: UserSearchResult = {
        users: backendData.users || [],
        // Note: total count is not provided by backend for performance reasons
        // (counting large Firestore collections is expensive). Set to 0 as we use
        // cursor-based pagination with hasMore/nextOffset for infinite scrolling.
        total: 0,
        hasMore: backendData.pagination?.has_more || false,
        nextOffset: backendData.pagination?.next_cursor,
      };

      return transformedData;
    },
    getNextPageParam: lastPage => {
      // Extract next cursor from pagination metadata
      // Return undefined when there are no more pages
      return lastPage.hasMore ? lastPage.nextOffset : undefined;
    },
    initialPageParam: undefined as string | undefined,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook for getting a specific user profile (admin access)
 */
export function useAdminUserProfile(uid: string) {
  return useQuery({
    queryKey: adminQueryKeys.user(uid),
    queryFn: async () => {
      const response = await authenticatedApi.getUserProfile(uid);
      if (!response.success || !response.data) {
        throw new Error(
          String(response.error) || 'Failed to fetch user profile'
        );
      }
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 2,
  });
}

/**
 * Hook for getting user's active entitlements (admin access)
 * Returns merged entitlement data with source attribution
 */
export function useAdminUserEntitlements(uid: string, enabled: boolean = true) {
  return useQuery({
    queryKey: adminQueryKeys.userEntitlements(uid),
    queryFn: async () => {
      const response = await authenticatedApi.getUserEntitlements(uid);
      if (!response.success || !response.data) {
        throw new Error(
          String(response.error) || 'Failed to fetch user entitlements'
        );
      }
      return response.data;
    },
    enabled, // Allow conditional fetching
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 2,
  });
}

/**
 * Hook for getting admin rate limit status
 */
export function useAdminRateLimitStatus() {
  return useQuery({
    queryKey: adminQueryKeys.rateLimitStatus(),
    queryFn: async () => {
      const response = await authenticatedApi.getAdminRateLimitStatus();
      if (!response.success || !response.data) {
        throw new Error(
          String(response.error) || 'Failed to fetch rate limit status'
        );
      }
      return response.data;
    },
    refetchInterval: 60 * 1000, // Refetch every minute
    staleTime: 30 * 1000, // 30 seconds
    retry: 1,
  });
}

/**
 * Hook for deleting user account (admin only)
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ uid, reason }: { uid: string; reason: string }) => {
      const response = await authenticatedApi.deleteUser(uid, reason);
      if (!response.success || !response.data) {
        throw new Error(String(response.error) || 'Failed to delete user');
      }
      return response.data;
    },
    onSuccess: () => {
      // Invalidate users list to refetch with deleted user removed
      queryClient.invalidateQueries({
        queryKey: adminQueryKeys.users(),
      });
    },
  });
}
//adding test line
